import { officialSources } from "./catalog";
import type { Locale } from "@/domain/schemas";

export interface LocalizedText {
  "zh-TW": string;
  en: string;
}

export const t = (value: LocalizedText, locale: Locale): string => value[locale];

export const categoryLabels: Record<string, LocalizedText> = {
  plate: { "zh-TW": "餐盤", en: "Plate" },
  bowl: { "zh-TW": "餐碗", en: "Bowl" },
  lunchbox: { "zh-TW": "餐盒", en: "Lunch box" },
  cutlery: { "zh-TW": "筷／匙", en: "Flatware" },
  "cup-bottle": { "zh-TW": "杯／瓶", en: "Cup & bottle" },
  recycled: { "zh-TW": "循環再生", en: "Recycled & regenerated" },
};

export const productNameEn: Record<string, string> = {
  "c-bento": "C-Bento",
  "cpet-tumbler-480": "CPET Reusable Tumbler 480 ml",
  "cpet-cup-200": "CPET Cup 200 ml",
  "cpet-five-compartment-tray": "CPET 5-Compartment Lunch Tray",
  "cpet-noodle-bowl": "CPET Noodle Bowl",
  "cpet-round-plate-large": "CPET Oval Plate",
  "cpet-chopsticks": "CPET Chopsticks",
  "cool-reusable-cup-700": "Tritan Reusable Tumbler 700 ml",
  "recycled-planter": "ReTritan Plant Pot",
};

export const industryContent = [
  {
    slug: "hospitality",
    icon: "Hotel",
    title: { "zh-TW": "飯店餐飲", en: "Hospitality" },
    summary: {
      "zh-TW":
        "官方應用頁以輕巧、耐摔、耐溫與可清洗的循環食器，對應飯店與餐旅營運情境。",
      en: "The official application page positions reusable tableware for hospitality operations.",
    },
  },
  {
    slug: "corporate",
    icon: "Building2",
    title: { "zh-TW": "企業", en: "Corporate" },
    summary: {
      "zh-TW": "從員工餐廳、企業贈禮到大宗採購，先釐清品項、數量、交付與回收需求。",
      en: "Start with product, quantity, delivery, customization, and recycling requirements.",
    },
  },
  {
    slug: "education",
    icon: "GraduationCap",
    title: { "zh-TW": "教育", en: "Education" },
    summary: {
      "zh-TW": "針對校園與團膳情境，整合產品選型、清洗流程與採購詢問。",
      en: "Explore product selection and procurement for education and group dining settings.",
    },
  },
  {
    slug: "government",
    icon: "Landmark",
    title: { "zh-TW": "政府", en: "Government" },
    summary: {
      "zh-TW": "提供需求盤點與文件詢問入口；採購規範與認證適用範圍需逐案確認。",
      en: "Use the enquiry path to confirm procurement documents and certification scope.",
    },
  },
  {
    slug: "medical",
    icon: "HeartPulse",
    title: { "zh-TW": "醫療", en: "Health facility" },
    summary: {
      "zh-TW": "依實際使用、清洗與責任要求評估，DEMO 不擴張任何醫療適用主張。",
      en: "Assess actual use, cleaning, and responsibility requirements case by case.",
    },
  },
  {
    slug: "aviation",
    icon: "Plane",
    title: { "zh-TW": "航空", en: "Airline" },
    summary: {
      "zh-TW": "以官方市場應用分類建立詢價入口；規格、驗證與履約條件待專案確認。",
      en: "Begin a project enquiry for specifications, validation, and fulfilment requirements.",
    },
  },
  {
    slug: "outdoor",
    icon: "TentTree",
    title: { "zh-TW": "戶外", en: "Outdoor" },
    summary: {
      "zh-TW": "探索可重複使用的杯、瓶與餐具，並依活動或場域需求提出詢問。",
      en: "Explore reusable cups, bottles, and tableware for outdoor settings.",
    },
  },
] as const;

export const oemSteps = [
  { zh: "詢問", en: "Enquiry" },
  { zh: "確認產品需求", en: "Confirm requirements" },
  { zh: "來回設計確認", en: "Design confirmation" },
  { zh: "3D 模型確認", en: "3D model confirmation" },
  { zh: "開模", en: "Tooling" },
  { zh: "打樣確認", en: "Sample confirmation" },
  { zh: "大量生產", en: "Mass production" },
  { zh: "出貨", en: "Shipment" },
  { zh: "售後服務", en: "After-sales service" },
] as const;

export const materialContent = {
  cpet: {
    title: { "zh-TW": "CPET（結晶 PET）", en: "CPET (Crystallized PET)" },
    eyebrow: { "zh-TW": "永續材質", en: "Sustainable material" },
    summary: {
      "zh-TW":
        "官方材質頁說明：CPET 經物理結晶製程，保留 PET 輕巧、耐摔與可回收特性，並提供耐熱與使用指引。",
      en: "The official material page describes CPET as crystallized PET with reuse and recycling characteristics.",
    },
    facts: [
      { zh: "官方頁列示耐高溫 140℃、耐低溫 -20℃。", en: "The official page lists 140°C heat resistance and -20°C low-temperature resistance." },
      { zh: "官方頁提供微波、洗碗機與消毒使用說明。", en: "The official page provides microwave, dishwasher, and sterilization guidance." },
      { zh: "實際使用仍須遵照各商品與電器說明。", en: "Always follow the specific product and appliance instructions." },
    ],
    sourceUrl: officialSources.cpet,
  },
  tritan: {
    title: { "zh-TW": "Tritan（改質 PCT）", en: "Tritan" },
    eyebrow: { "zh-TW": "永續材質", en: "Sustainable material" },
    summary: {
      "zh-TW":
        "官方材質與產品頁將 Tritan 應用於餐盤、餐盒、杯與瓶等可重複使用食器；各 SKU 的耐溫與使用方式需以正式商品資料為準。",
      en: "The official site applies Tritan to reusable plates, lunch boxes, cups, and bottles.",
    },
    facts: [
      { zh: "官方認證頁列有 Tritan 原料與產品檢測文件。", en: "The official certification page lists material and product test documents." },
      { zh: "認證適用範圍不得自動擴張到所有 SKU。", en: "Certification scope must not be extended to every SKU automatically." },
      { zh: "回收合作需依材質、數量與現場條件個別評估。", en: "Recycling cooperation is assessed by material, quantity, and operating conditions." },
    ],
    sourceUrl: officialSources.tritan,
  },
  "sugarcane-fiber": {
    title: { "zh-TW": "甘蔗纖維", en: "Sugarcane Fiber" },
    eyebrow: { "zh-TW": "永續材質", en: "Sustainable material" },
    summary: {
      "zh-TW":
        "官方 B 站將甘蔗纖維列為永續材質系列。本 DEMO 只建立內容與詢價入口，不自行補上未核對的規格或認證。",
      en: "The official B2B site lists sugarcane fiber as a sustainability material series.",
    },
    facts: [
      { zh: "產品型號與供貨狀態待正式資料匯入。", en: "Product models and availability await formal data import." },
      { zh: "耐熱、微波與食品接觸資料需依個別產品文件確認。", en: "Heat, microwave, and food-contact details require product-specific documents." },
      { zh: "可透過企業詢價表提出應用與數量需求。", en: "Use the business enquiry form for application and quantity requirements." },
    ],
    sourceUrl: officialSources.sugarcane,
  },
} as const;

export const certificationGroups = [
  {
    title: { "zh-TW": "Tritan 原料", en: "Tritan material" },
    items: [
      "歐盟 RoHS 有害物質限用指令－原料 Tritan（改質 PCT）",
      "美國 FDA 認證－原料 Tritan（改質 PCT）TX1001",
      "SGS 檢測報告－原料 Tritan（改質 PCT）",
    ],
  },
  {
    title: { "zh-TW": "Tritan 產品", en: "Tritan products" },
    items: [
      "SGS 檢測報告－暖心隨身瓶",
      "SGS 檢測報告－杯、湯碗、飯碗、餐盤",
      "中華民國食品容器規範（塑膠中心）檢測報告－餐盤",
      "SGS 合格證書（EU 歐盟規範）－Tritan 大圓盤",
    ],
  },
  {
    title: { "zh-TW": "CPET 產品", en: "CPET products" },
    items: [
      "SGS 檢測報告－CPET 潔晶麵碗",
      "SGS 檢測報告－CPET 潔晶拉麵碗（140℃ 耐熱測試）",
      "SGS 合格證書（EU 歐盟規範）－CPET 麵碗",
      "SGS 檢測報告－CPET 潔晶飯碗、杯、圓盤",
    ],
  },
] as const;

export const knowledgeArticles = [
  {
    slug: "cpet-guide",
    category: "materials",
    title: {
      "zh-TW": "CPET 食器：從材質到日常使用",
      en: "CPET tableware: material and everyday use",
    },
    summary: {
      "zh-TW": "整理官方材質頁的結晶 PET、耐溫、清洗與回收使用重點。",
      en: "A source-led overview of crystallized PET, temperature, cleaning, and recycling guidance.",
    },
    sourceUrl: officialSources.cpet,
    checkedAt: "2026-07-24",
  },
  {
    slug: "tritan-guide",
    category: "materials",
    title: {
      "zh-TW": "Tritan 食器與回收評估",
      en: "Tritan tableware and recycling assessment",
    },
    summary: {
      "zh-TW": "從官方產品分類、原料文件與回收機制理解可重複使用食器。",
      en: "Understand reusable tableware through official product, material, and recycling sources.",
    },
    sourceUrl: officialSources.tritan,
    checkedAt: "2026-07-24",
  },
  {
    slug: "closed-loop-recycling",
    category: "recycling",
    title: {
      "zh-TW": "封閉式循環回收如何開始",
      en: "How a closed-loop recycling programme begins",
    },
    summary: {
      "zh-TW": "先盤點材質、數量、回收地點與週期，再由企業合作流程確認可行性。",
      en: "Start with material, quantity, collection location, and cycle, then confirm feasibility.",
    },
    sourceUrl: officialSources.b2bHome,
    checkedAt: "2026-07-24",
  },
] as const;

export const faqItems = [
  {
    q: { "zh-TW": "CPET 餐具可以放入微波爐使用嗎？", en: "Can CPET tableware be used in a microwave?" },
    a: {
      "zh-TW":
        "官方 CPET 頁面說明可以微波加熱，並建議控制時間與功率；實際使用仍應遵照商品與微波爐說明。",
      en: "The official CPET page includes microwave guidance. Follow both the product and microwave instructions.",
    },
  },
  {
    q: { "zh-TW": "CPET 餐具可以用洗碗機清洗嗎？", en: "Can CPET tableware be washed in a dishwasher?" },
    a: {
      "zh-TW": "官方頁面說明可用洗碗機清洗；請依個別商品與設備條件使用。",
      en: "The official page includes dishwasher guidance; use according to the specific product and equipment.",
    },
  },
  {
    q: { "zh-TW": "如何提出大宗採購或客製需求？", en: "How do I request bulk purchasing or customization?" },
    a: {
      "zh-TW": "填寫企業詢價表，提供品項、數量、交付地、日期、預算與 Logo／回收需求。DEMO 只在本機模擬提交。",
      en: "Use the business enquiry form with product, quantity, destination, timing, budget, branding, and recycling needs.",
    },
  },
  {
    q: { "zh-TW": "網站上的價格與庫存是即時的嗎？", en: "Are price and inventory live?" },
    a: {
      "zh-TW": "DEMO 價格來自 2026-07-24 核對的官方頁面，庫存與正式交易仍須由正式商品 API／後台提供。",
      en: "DEMO prices were checked against official pages on 2026-07-24. Live inventory and transactions require the production catalogue.",
    },
  },
  {
    q: { "zh-TW": "認證是否適用所有商品？", en: "Do certifications apply to every product?" },
    a: {
      "zh-TW": "不一定。認證與檢測需依原料、品項、報告編號與有效範圍逐一對應。",
      en: "Not necessarily. Each document must be mapped to its material, product, report identifier, and valid scope.",
    },
  },
] as const;

export const legalPendingNotice: LocalizedText = {
  "zh-TW": "集力／集泉在品牌、製造、銷售、發票與個資主體上的責任分工待客戶確認。",
  en: "The roles of Living Forward and Living Fountain across brand, manufacturing, sales, invoicing, and privacy remain subject to client confirmation.",
};
